// Aufgabe anlegen — ein Modal für alle Stellen im CRM.
//
// Anforderung R9: „tasks can be created from different locations in the CRM to
// quickly create a reminder for a follow up".
//
// Unterstützt:
// - Fälligkeitsdatum über Preset-Dropdown (Heute, Morgen, in 2/3 Werktagen, 1/2 Wochen, 1/3/6 Monaten, 1 Jahr, Benutzerdefiniert)
// - Uhrzeit-Auswahl für die Fälligkeit
// - Benutzerdefiniertes Datum mit Datepicker darunter
// - Prioritäts-Auswahl (Keine, Niedrig, Mittel, Hoch)
// - Zuordnung zu mehreren Datensätzen (Firma, Kontaktperson, mehrere Kundenanfragen, zukünftig Angebote/Aufträge)
//
// Dasselbe Fenster BEARBEITET auch: mit `task` kommt es vorbelegt, speichert per
// `updateTasks` und bietet Löschen an. Ein zweites Formular für dieselben Felder wäre
// eine zweite Stelle, an der ein neues Feld vergessen wird.
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import ActivityDocumentsField from "./ActivityDocumentsField";
import RichNoteField, { noteValue } from "./RichNoteField";
import { documentsOf, saveActivityDocuments } from "./activityDocuments";
import CompanySearch from "./CompanySearch";
import ConfirmationModal from "./ConfirmationModal";
import RequestSelect from "./RequestSelect";
import { client } from "../helpers/amplifyClient";
import {
  deleteTask,
  hydrateTask,
  withTaskSortKey,
  taskObjectIds,
  TASK_STATUSES,
} from "../helpers/tasks";
import { ordersByCompanyID } from "./graphql/activityFeed.queries";
import { DEMAND_TASK_TYPE, COMPANY_PAUSE_DAYS } from "../helpers/demandInputs";
import * as queries from "./graphql/taskForm.queries";
import {
  TASK_TYPES,
  PRIORITIES,
  DUE_PRESETS,
  REMINDER_OFFSETS,
  calculatePresetDate,
  formatDateLocal,
  formatTimeLocal,
  combineDateAndTimeToIso,
} from "./taskDateHelpers";

// Reihenfolge egal: es geht um die Menge der Verknüpfungen, nicht um ihre Sortierung.
export const sameIds = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  const sortedB = [...b].sort();
  return [...a].sort().every((id, index) => id === sortedB[index]);
};

const TaskFormModal = (props) => {
  const { t } = useTranslation(["tasks", "common", "components"]);
  const { user } = useSelector((state) => ({ user: state.Auth.user }));
  // Kontext: was der Aufrufer schon weiss. Alles optional.
  const {
    companyId: initialCompanyId,
    objectId,
    objectType,
    contactPersonId: initialContactPersonId,
    onClose,
    onCreated,
    // Vorhandene Aufgabe -> Bearbeiten statt Anlegen. Muss `id` tragen, alles andere
    // ist optional; was fehlt, startet leer.
    task,
    // Stapel: fuer JEDE dieser Firmen entsteht dieselbe Aufgabe. Blendet den ganzen
    // Block „Zugehoerige Datensaetze" aus — die Verknuepfung kommt aus der Auswahl.
    companies,
  } = props;
  const editing = Boolean(task?.id);
  const batchCompanies = companies || [];
  const batch = batchCompanies.length > 0;
  const [docs, setDocs] = useState(() => documentsOf(task));

  const [subject, setSubject] = useState(task?.subject || "");
  const [body, setBody] = useState(task?.body || "");
  const [taskType, setTaskType] = useState(task?.taskType || "todo");
  const [priority, setPriority] = useState(task?.priority || ""); // "", "low", "medium", "high"
  // Erledigen direkt hier, statt nur ueber die Listen: aus dem Verlauf heraus ist
  // dieses Fenster die einzige Stelle, an der die Aufgabe erreichbar ist.
  const [status, setStatus] = useState(task?.status || "open");

  // Fälligkeitsdatum Presets & Uhrzeit. Beim Bearbeiten gibt es kein Preset mehr, das
  // zum gespeicherten Datum passen würde — also "custom" mit genau diesem Datum.
  const [duePreset, setDuePreset] = useState(task?.dueDate ? "custom" : "today");
  const [customDate, setCustomDate] = useState(
    task?.dueDate
      ? task.dueDate.substring(0, 10)
      : formatDateLocal(calculatePresetDate("today"))
  );
  const [dueTime, setDueTime] = useState(formatTimeLocal(task?.dueDate));

  // Erinnerung an die Faelligkeit. `null` in der Datenbank heisst „keine" — deshalb
  // haengt der Schalter an `!= null` und nicht an einem eigenen Feld: ein zweites Feld
  // waere eine zweite Stelle, an der „an, aber ohne Vorlauf" entstehen kann.
  const [remind, setRemind] = useState(task?.reminderMinutesBefore != null);
  const [reminderMinutes, setReminderMinutes] = useState(
    task?.reminderMinutesBefore ?? 0
  );

  // Vorbelegt mit einem selbst (R9): der Normalfall ist „ich mache das". „Niemand"
  // steht als erste Option darüber und ist mit einem Klick erreichbar.
  const [assignedUserId, setAssignedUserId] = useState(
    editing ? task.assignedUserId || "" : user.id
  );
  const [people, setPeople] = useState([]);

  // Verknüpfungen: Firma & Ansprechpartner
  const [companyId, setCompanyId] = useState(task?.companyID || initialCompanyId || "");
  const [companyData, setCompanyData] = useState(() => {
    if (props.company?.id && props.company?.name) {
      return { id: props.company.id, name: props.company.name };
    }
    if (task?.company?.id && task?.company?.name) {
      return { id: task.company.id, name: task.company.name };
    }
    return null;
  });
  const [contactPersonId, setContactPersonId] = useState(
    task?.contactPersonId || initialContactPersonId || ""
  );
  const [contacts, setContacts] = useState([]);

  // Verknüpfungen: Kundenanfragen und Aufträge (je mehrere). Die Typen, die eine Liste
  // im Formular haben — alles andere (Produktanfrage, Sendung, ...) bleibt unberührt.
  const LISTED_TYPES = ["CustomerRequest", "Order"];
  const [requests, setRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  // associatedObjectIds ist die Mehrfachauswahl; objectID ist dieselbe Verknüpfung in
  // der Einzahl und bei älteren Aufgaben das Einzige, was gesetzt ist (taskObjectIds).
  const seedIds = (type) => () =>
    editing
      ? taskObjectIds(task, type)
      : objectId && (objectType || "CustomerRequest") === type
        ? [objectId]
        : [];
  const [selectedRequestIds, setSelectedRequestIds] = useState(seedIds("CustomerRequest"));
  const [selectedOrderIds, setSelectedOrderIds] = useState(seedIds("Order"));
  // Ausgangsstand, um beim Speichern zu erkennen, ob an der Verknüpfung überhaupt
  // gerührt wurde. Haengt die Aufgabe an einem Typ ohne Liste (Sendung, ...), steht er
  // in keiner Auswahl und würde ungefragt mitgeschrieben verloren gehen.
  const [initialRequestIds] = useState(seedIds("CustomerRequest"));
  const [initialOrderIds] = useState(seedIds("Order"));

  const [loadingCompanyContext, setLoadingCompanyContext] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Nur fuer Bedarfsaufgaben: beim Loeschen zusaetzlich den ganzen Kunden einen
  // Monat stillstellen. Bewusst nicht vorangehakt — das trifft alle seine Artikel.
  const [pauseCompany, setPauseCompany] = useState(false);

  // Bearbeiter laden
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const peopleResponse = await client.graphql({
          query: queries.customListContactPersons,
          variables: { type: "c", country: "DE" },
        });
        if (cancelled) return;
        const list = peopleResponse.data.customListContactPersons ?? [];
        // Sich selbst notfalls ergaenzen. Die Lambda liefert nur type "c"/"cs" und
        // country "DE" — wer da herausfaellt, saehe seine eigene Vorauswahl sonst
        // nicht in der Liste und das Feld stuende scheinbar auf „Niemand", obwohl
        // die Aufgabe an ihn ginge.
        setPeople(
          list.some((person) => person.id === user.id)
            ? list
            : [
                {
                  id: user.id,
                  firstName: user.firstName || "",
                  lastName: user.lastName || "",
                },
                ...list,
              ]
        );
      } catch (err) {
        console.error("TaskFormModal people error:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user.id, user.firstName, user.lastName]);

  // Firmendaten nachladen, falls die Firma nur als ID bekannt ist (Aufrufer-Kontext
  // oder bearbeitete Aufgabe ohne mitgeliefertes company-Objekt)
  useEffect(() => {
    let cancelled = false;
    if (companyId && (!companyData || companyData.id !== companyId || !companyData.name)) {
      (async () => {
        try {
          const res = await client.graphql({
            query: queries.getCompanyBasic,
            variables: { id: companyId },
          });
          if (cancelled) return;
          const comp = res?.data?.getCompanies;
          if (comp) {
            setCompanyData({ id: comp.id, name: comp.name });
          }
        } catch (err) {
          console.error("TaskFormModal company fetch error:", err);
        }
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [companyId, companyData]);

  // Kontakte und Anfragen der gewählten Firma laden
  useEffect(() => {
    let cancelled = false;
    if (!companyId) {
      setContacts([]);
      setRequests([]);
      setOrders([]);
      return;
    }

    setLoadingCompanyContext(true);
    (async () => {
      try {
        const [contactsResponse, requestsResponse, ordersResponse] = await Promise.all([
          client.graphql({
            query: queries.companiesContactPersonsByCompanyID,
            variables: { companyID: companyId },
          }),
          client.graphql({
            query: queries.customerRequestsByCompanyID,
            variables: { companyID: companyId },
          }),
          // Eigener catch: wer keine Auftraege lesen darf, behaelt Kontakte und Anfragen.
          client
            .graphql({ query: ordersByCompanyID, variables: { companyId, limit: 100 } })
            .catch((error) => {
              console.log("TaskFormModal: Auftraege nicht lesbar", error);
              return null;
            }),
        ]);

        if (cancelled) return;

        const fetchedContacts =
          contactsResponse?.data?.companiesContactPersonsByCompanyID?.items || [];
        const fetchedRequests =
          requestsResponse?.data?.customerRequestsByComanyIdByCreatedAt?.items || [];

        setContacts(fetchedContacts);
        setRequests(fetchedRequests);
        setOrders(ordersResponse?.data?.ordersByCompanyIdByOrderNumber?.items || []);
        // Ein vorbelegter Kontakt, den die Firma nicht (mehr) fuehrt — geloescht,
        // als "former" ausgesteuert oder jenseits der 200er-Seite — hat KEINE
        // Option im Feld. Das Feld zeigt dann die erste, also "Keiner", der Zustand
        // haelt aber weiter die ID: gespeichert wird ein Ansprechpartner, den der
        // Nutzer nie gesehen hat. Im Mail-Fenster steht danach seine Adresse als
        // Empfaenger, obwohl die Aufgabe "ohne Kontakt" angelegt wurde.
        setContactPersonId((current) =>
          current && !fetchedContacts.some((contact) => contact.id === current)
            ? ""
            : current
        );
      } catch (err) {
        console.error("TaskFormModal company context error:", err);
      } finally {
        if (!cancelled) setLoadingCompanyContext(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const handlePresetChange = (e) => {
    const nextPreset = e.target.value;
    setDuePreset(nextPreset);
    if (nextPreset !== "custom") {
      setCustomDate(formatDateLocal(calculatePresetDate(nextPreset)));
    }
  };

  const handleCompanySelect = (selection) => {
    const nextCompany = selection?.data || (selection?.value ? { id: selection.value, name: selection.label } : null);
    setCompanyData(nextCompany);
    const nextId = nextCompany?.id || "";
    setCompanyId(nextId);
    // Bei Wechsel der Firma bisherigen Kontakt und ausgewählte Anfragen zurücksetzen
    if (nextId !== companyId) {
      setContactPersonId("");
      setSelectedRequestIds([]);
      setSelectedOrderIds([]);
    }
  };

  // Im Stapel liefert jede Zeile der Auswahl ihre Firma — das Feld kann nicht fehlen.
  const missingCompany = !batch && taskType !== "todo" && !companyId;

  const submit = async (event) => {
    event.preventDefault();
    if (!subject.trim()) return;
    if (missingCompany) {
      toast.error(t("Please select a company for this task type."));
      return;
    }
    setSaving(true);
    try {
      const targetDateStr =
        duePreset === "custom"
          ? customDate
          : formatDateLocal(calculatePresetDate(duePreset));
      const isoDueDate = combineDateAndTimeToIso(targetDateStr, dueTime);

      const requestsChanged =
        !editing ||
        !sameIds(selectedRequestIds, initialRequestIds) ||
        !sameIds(selectedOrderIds, initialOrderIds);

      // Ohne Auswahl bleibt die Verknuepfung des Aufrufers stehen, wenn ihr Typ KEINE
      // Liste im Formular hat (Produktanfrage, Sendung, ...) — sie waere sonst mit dem
      // Speichern verloren. Nur beim Anlegen: beim Bearbeiten heisst eine geleerte
      // Auswahl „Verknuepfung entfernen" und darf nicht aus dem Kontext zurueckkehren.
      const fallbackId =
        editing || LISTED_TYPES.includes(objectType || "CustomerRequest") ? "" : objectId || "";
      const primaryRequestId = selectedRequestIds[0] || selectedOrderIds[0] || fallbackId;
      const primaryRequestType = selectedRequestIds[0]
        ? "CustomerRequest"
        : selectedOrderIds[0]
          ? "Order"
          : primaryRequestId
            ? objectType || "CustomerRequest"
            : "";
      // Haengt die Aufgabe an einem Angebot, einer Produktanfrage oder einem Auftrag,
      // bleibt DAS ihr Objekt — die Anfragen-Auswahl aendert nur associatedObjectIds.
      // Sonst verlor eine Angebots-Aufgabe beim Umhaengen der Anfragen ihr Angebot,
      // und die Work Session landete auf der Firma statt auf dem Angebot.
      const ownType = editing ? task.objectType : objectType;
      const ownId = editing ? task.objectID : objectId;
      // Auftraege stehen inzwischen selbst als Liste im Formular — eine Auftrags-Aufgabe
      // behaelt ihren Auftrag ueber die Auswahl, und eine geleerte Auswahl entfernt ihn.
      const keepOwnObject = Boolean(ownId && ownType && !LISTED_TYPES.includes(ownType));

      // Der Typ je ID steht nur hier — taskObjectIds liest ihn zurueck.
      const linked = [
        ...selectedRequestIds.map((id) => ({
          id,
          type: "CustomerRequest",
          number: requests.find((r) => r.id === id)?.number || id,
        })),
        ...selectedOrderIds.map((id) => ({
          id,
          type: "Order",
          number: orders.find((o) => o.id === id)?.orderNumber || id,
        })),
      ];
      const associatedObjects = linked.length > 0 ? JSON.stringify(linked) : null;

      // Beim ANLEGEN werden leere Felder weggelassen (Regel 3). Beim BEARBEITEN müssen
      // sie als null mitkommen, sonst bleibt ein entfernter Kontakt oder eine geleerte
      // Priorität in der Datenbank stehen. `Tasks` hat nur Model-@auth, null darf hier
      // durch — anders als bei Companies oder Activities (CLAUDE.md).
      const optional = (value) => (value ? value : editing ? null : undefined);

      // Erst die Dateien, dann der Datensatz: die Anhaenge stehen als Feld IN der
      // Aufgabe (kein @hasMany). Beim Bearbeiten immer mitschicken, auch leer — sonst
      // laesst sich der letzte Anhang nicht entfernen.
      const documents = await saveActivityDocuments({
        docs,
        companyId,
        country: user?.country,
      });

      const input = {
        ...(editing && { id: task.id }),
        ...(editing || documents.length > 0 ? { documents } : {}),
        subject: subject.trim(),
        taskType,
        // Ersteller gehört zum Anlegen. Der Status kommt aus dem Schalter in der
        // ersten Zeile — `withTaskSortKey` unten braucht ihn ohnehin zusammen mit
        // dueDate, sonst bricht der Resolver des zusammengesetzten Sort Keys ab.
        status,
        completedAt:
          status === "completed"
            ? task?.completedAt || new Date().toISOString()
            : null,
        ...(editing ? {} : { assigneeType: "user", createdByUserId: user.id }),
        priority: optional(priority),
        body: optional(noteValue(body)),
        dueDate: optional(isoDueDate),
        // Erinnerung. Beim Bearbeiten immer mitschicken, sonst laesst sie sich nicht
        // abschalten. Den Versandstempel nimmt `withTaskSortKey` zurueck.
        reminderMinutesBefore: remind ? Number(reminderMinutes) : editing ? null : undefined,
        assignedUserId: optional(assignedUserId),
        companyID: optional(companyId),
        contactPersonId: optional(contactPersonId),
        // Nur schreiben, wenn die Auswahl angefasst wurde — siehe initialRequestIds.
        ...(requestsChanged
          ? {
              objectID: keepOwnObject ? ownId : optional(primaryRequestId),
              objectType: keepOwnObject
                ? ownType
                : optional(primaryRequestId ? primaryRequestType : ""),
              associatedObjectIds:
                linked.length > 0 ? linked.map((o) => o.id) : optional(""),
              associatedObjects: optional(associatedObjects),
            }
          : {}),
      };
      for (const key of Object.keys(input)) {
        if (input[key] === undefined) delete input[key];
      }

      // Stapel: dieselbe Eingabe, einmal pro Firma. `allSettled` statt `all` — sonst
      // verschweigt der erste Fehlschlag, dass die uebrigen Aufgaben schon stehen.
      // ponytail: die Anhaenge werden EINMAL hochgeladen und von allen Aufgaben
      // geteilt. Ceiling: wer sie an einer Aufgabe loescht, loescht die Datei fuer
      // alle. Upgrade-Pfad: pro Firma hochladen, wenn das jemand stoert.
      if (batch) {
        const results = await Promise.allSettled(
          batchCompanies.map((company) =>
            client.graphql({
              query: queries.createTasks,
              variables: { input: { ...input, companyID: company.id } },
            }),
          ),
        );
        const created = results.filter((r) => r.status === "fulfilled").length;
        const failed = results.length - created;
        if (created > 0) {
          toast.success(
            t("{{count}} / {{total}} tasks created.", {
              count: created,
              total: results.length,
            }),
          );
        }
        if (failed > 0) {
          console.error(
            "TaskFormModal batch errors:",
            results.filter((r) => r.status === "rejected").map((r) => r.reason),
          );
          toast.error(t("Could not create the task."));
        }
        onCreated?.(null);
        onClose();
        return;
      }

      const response = await client.graphql({
        query: editing ? queries.updateTasks : queries.createTasks,
        // Beim Anlegen stehen status und dueDate ohnehin beide drin.
        variables: { input: editing ? withTaskSortKey(task, input) : input },
      });

      // `Tasks` hat keine @hasOne-Verbindungen mehr — die Antwort traegt weder Firma
      // noch Kontakt. Ohne dieses Nachtragen stuende in der Zeile des Aufrufers die
      // ALTE Firma und der alte Kontakt (Work Session, Verlauf, Queue).
      const saved = await hydrateTask(
        editing ? response.data.updateTasks : response.data.createTasks,
      );

      toast.success(editing ? t("Task updated.") : t("Task created."));
      onCreated?.(saved);
      onClose();
    } catch (err) {
      console.error("TaskFormModal save error:", err);
      toast.error(editing ? t("Could not update the task.") : t("Could not create the task."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await deleteTask(task, { pauseCompany: pauseCompany });
      toast.success(t("Task deleted."));
      // Derselbe Rückkanal wie beim Speichern: die Aufrufer laden danach neu bzw.
      // nehmen die Zeile aus der Liste. Ein zweiter Callback wäre eine zweite Stelle,
      // die jeder Aufrufer verdrahten müsste.
      onCreated?.(null);
      onClose();
    } catch (err) {
      console.error("TaskFormModal delete error:", err);
      toast.error(t("Could not delete the task."));
    } finally {
      setSaving(false);
      setConfirmDelete(false);
    }
  };

  return (
    <>
    {/* Waehrend die Loesch-Rueckfrage steht, tritt dieses Fenster zur Seite statt sich
        darunter zu stapeln. Der Formularzustand haengt an dieser Komponente und nicht
        am Modal — Abbrechen setzt `confirmDelete` zurueck und alles steht wieder da. */}
    <Modal
      show={!confirmDelete}
      onHide={onClose}
      backdrop="static"
      size="lg"
      className="modal-nested"
      backdropClassName="modal-nested-backdrop"
    >
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editing
              ? t("Edit task")
              : batch
                ? t("New task for {{count}} companies", { count: batchCompanies.length })
                : t("New task")}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Betreff & Status nebeneinander */}
          <Row className="g-2 mb-3">
            <Col xs={12} md={8}>
              <Form.Label>
                {t("Subject")} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                autoFocus
                required
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t("e.g. Follow up on the offer")}
              />
            </Col>
            <Col xs={12} md={4}>
              <Form.Label>{t("Status")}</Form.Label>
              <Form.Select
                id={editing ? `task-status-${task.id}` : "task-status-new"}
                value={status}
                disabled={saving}
                onChange={(e) => setStatus(e.target.value)}
              >
                {TASK_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {t(s.label)}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          {/* Typ & Priorität nebeneinander */}
          <Row className="g-2 mb-3">
            <Col xs={12} md={6}>
              <Form.Label>{t("Type")}</Form.Label>
              <Form.Select value={taskType} onChange={(e) => setTaskType(e.target.value)}>
                {TASK_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(type)}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={6}>
              <Form.Label>{t("Priority")}</Form.Label>
              <Form.Select value={priority} onChange={(e) => setPriority(e.target.value)}>
                {PRIORITIES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {t(p.label)}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          {/* Fälligkeitsdatum Dropdown & Uhrzeit */}
          <Row className="g-2 mb-3">
            <Col xs={12} md={7}>
              <Form.Label>{t("Due Date")}</Form.Label>
              <Form.Select value={duePreset} onChange={handlePresetChange}>
                {DUE_PRESETS.map((preset) => (
                  <option key={preset.value} value={preset.value}>
                    {t(preset.label)}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={5}>
              <Form.Label>{t("Due Time")}</Form.Label>
              <Form.Control
                type="time"
                value={dueTime}
                onChange={(e) => setDueTime(e.target.value)}
              />
            </Col>
          </Row>

          {/* Benutzerdefinierter Kalender-Picker */}
          {duePreset === "custom" && (
            <Form.Group className="mb-3">
              <Form.Label>{t("Select Date")}</Form.Label>
              <Form.Control
                type="date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
              />
            </Form.Group>
          )}

          {/* Erinnerung an die Fälligkeit */}
          <Row className="g-2 mb-3 align-items-end">
            <Col xs={12} md={7}>
              <Form.Check
                type="checkbox"
                id="task-remind"
                label={t("Remind me about this task")}
                checked={remind}
                onChange={(e) => setRemind(e.target.checked)}
              />
            </Col>
            {remind && (
              <Col xs={12} md={5}>
                <Form.Label>{t("Remind")}</Form.Label>
                <Form.Select
                  value={reminderMinutes}
                  onChange={(e) => setReminderMinutes(Number(e.target.value))}
                >
                  {REMINDER_OFFSETS.map((offset) => (
                    <option key={offset.value} value={offset.value}>
                      {t(offset.label)}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            )}
          </Row>

          <hr className="my-3" />

          {/* Bearbeiter */}
          <Form.Group className="mb-3">
            <Form.Label>{t("Assignee")}</Form.Label>
            <Form.Select
              value={assignedUserId}
              onChange={(e) => setAssignedUserId(e.target.value)}
            >
              <option value="">{t("Unassigned")}</option>
              {people.map((person) => (
                <option key={person.id} value={person.id}>
                  {[person.firstName, person.lastName].filter(Boolean).join(" ")}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Notiz */}
          <Form.Group className="mb-3">
            <Form.Label>{t("Note")}</Form.Label>
            <RichNoteField rows={3} value={body} onChange={setBody} />
          </Form.Group>

          <hr className="my-3" />

          {/* Zugehörige Datensätze: Firma, Ansprechpartner, Kundenanfragen */}
          <h6 className="text-uppercase text-muted mb-3 font-13">
            {t("Associated records")}
          </h6>

          {batch ? (
            <div className="mb-3">
              <p className="text-muted small mb-2">
                {t("One task is created per selected company.")}
              </p>
              <div
                className="border rounded p-2 bg-light bg-opacity-25"
                style={{ maxHeight: 180, overflowY: "auto" }}
              >
                {batchCompanies.map((company) => (
                  <div key={company.id} className="small">
                    {company.name || company.id}
                  </div>
                ))}
              </div>
            </div>
          ) : (
          <>
          {/* Firma */}
          <Form.Group className="mb-3">
            <Form.Label>
              {t("Company")}
              {taskType !== "todo" && <span className="text-danger ms-1">*</span>}
            </Form.Label>
            <CompanySearch
              type="customerOrLead"
              // An einem Lead haengen Aufgaben, Anrufe und Termine ganz normal — das
              // Ausgrauen aus A-V Punkt 28 gilt nur fuer Anfrage/Angebot/Artikel.
              // Ohne das laesst sich die Firma einer Aufgabe nicht auf einen Lead
              // setzen: der Treffer steht da, ist aber nicht anklickbar.
              allowLeads
              defaultValue={companyData}
              placeholder={t("Search company...")}
              onSelect={handleCompanySelect}
              isClearable
              showClear
            />
            {missingCompany && (
              <Form.Text className="text-danger small">
                {t("A company is required for this task type.")}
              </Form.Text>
            )}
          </Form.Group>

          {/* Kontaktperson */}
          {companyId && (
            <Form.Group className="mb-3">
              <Form.Label>
                {t("Contact")}
                {(taskType === "call" || taskType === "email") && (
                  <span className="text-muted ms-1">
                    {t("— needed for dialling and e-mail")}
                  </span>
                )}
              </Form.Label>
              <Form.Select
                value={contactPersonId}
                onChange={(e) => setContactPersonId(e.target.value)}
                disabled={loadingCompanyContext}
              >
                <option value="">{t("None")}</option>
                {contacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {[contact.firstName, contact.lastName].filter(Boolean).join(" ") ||
                      contact.email ||
                      contact.id}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          {/* Kundenanfragen und Aufträge (Mehrfachauswahl, je ein Tab) */}
          {companyId && (
            <RequestSelect
              key={companyId}
              requests={requests}
              loading={loadingCompanyContext}
              isMulti
              values={selectedRequestIds}
              onChange={setSelectedRequestIds}
              orders={orders}
              ordersLoading={loadingCompanyContext}
              orderValues={selectedOrderIds}
              onOrderChange={setSelectedOrderIds}
            />
          )}
          </>
          )}

          <hr className="my-3" />

          <ActivityDocumentsField
            docs={docs}
            setDocs={setDocs}
            companyId={companyId}
            className="compact-documents-field"
          />
        </Modal.Body>

        <Modal.Footer className={editing ? "justify-content-between" : undefined}>
          {editing && (
            <Button
              variant="outline-danger"
              onClick={() => setConfirmDelete(true)}
              disabled={saving}
              title={t("Delete task")}
            >
              <i className="mdi mdi-trash-can-outline" />
            </Button>
          )}
          {/* Kein Abbrechen: das X oben rechts tut dasselbe. */}
          <Button
            type="submit"
            disabled={saving || !subject.trim() || missingCompany}
          >
            {editing ? t("Save") : t("Create")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>

      {/* Neben und nicht IN dem Formular-Fenster: zwei gestapelte Modals verlassen
          sich sonst auf die Backdrop-Reihenfolge des Managers. */}
      {confirmDelete && (
        <ConfirmationModal
          showModal
          hideModal={() => setConfirmDelete(false)}
          title={t("Delete task")}
          message={t("Delete the task \"{{subject}}\"? This cannot be undone.", {
            subject: task.subject || "",
          })}
          confirmButtonText={t("Delete")}
          confirmButtonLoading={saving}
          confirmHandler={remove}
        >
          {/* Eine geloeschte Bedarfsaufgabe heisst "nicht jetzt" — aber fuer wen?
              Sie deckt alle faelligen Artikel des Kunden ab, es gibt also nichts
              abzuleiten. Gefragt wird deshalb nach dem Kunden, und der Nutzer
              antwortet. Nicht vorangehakt: eine Woche Ruhe setzt das Loeschen
              ohnehin (deleteTask), der Monat ist die Ausnahme. */}
          {task?.objectType === DEMAND_TASK_TYPE && (
            <Form.Check
              id="pause-company-reminders"
              checked={pauseCompany}
              onChange={(event) => setPauseCompany(event.target.checked)}
              label={t("Pause demand planning for this customer for {{days}} days", {
                days: COMPANY_PAUSE_DAYS,
              })}
            />
          )}
        </ConfirmationModal>
      )}
    </>
  );
};

export default TaskFormModal;
